import { TweenLite, Power2 } from 'gsap'

import EventHubMixin from '@/mixins/EventHub.js'
import APIMixin from '@/mixins/API.js'
import ScrollMixin from '@/mixins/Scroll.js'
import SizesMixin from '@/mixins/Sizes.js'

import AppPicture from '@/components/app-picture'
import AppVideo from '@/components/app-video'
import AppContent from '@/components/app-content'
import AppIconArrow from '@/components/app-icon-arrow'
import AppParallax from '@/components/app-parallax'
import AppScrollReveal from '@/components/app-scroll-reveal'

export default
{
    name: 'app-page-story',

    mixins:
    [
        EventHubMixin,
        APIMixin,
        ScrollMixin,
        SizesMixin
    ],

    components:
    {
        AppVideo,
        AppPicture,
        AppContent,
        AppIconArrow,
        AppParallax,
        AppScrollReveal
    },

    data()
    {
        return {
            content: null,
            story: null,
            nextStory: null,
            coverVideoPlaying: false,
            isShareVisible: false
        }
    },

    created()
    {
        this.fullHeight = 0
        this.$eventHub.$on('player.onDeactivate', this.onPlayerDeactivate)

        this.time = Date.now()

        // Fetch page content
        this.fetch('pages', { slug: 'stories', per_page: 1000 }, (data, error) =>
        {
            // Error
            if(error === true || data.length < 1)
            {
                this.$router.push({ name: '404' })
                return
            }

            this.content = data[0]

            // Header logo
            if(this.$root.$previousRoute && this.$root.$previousRoute.name === 'stories')
            {
                // Back to stories
                this.$eventHub.$emit('headerLogo.setState', { state: 'back', backText: this.content.acf.label_back_to_stories, backTo: { name: `${this.$route.meta.lang}_stories` } })
            }

            this.$nextTick(() =>
            {
                this.onResize()
            })
        })

        // Fetch all stories
        this.fetch('stories', { per_page: 1000 }, (data, error) =>
        {
            // Error
            if(error === true || data.length < 1)
            {
                this.$router.push({ name: '404' })
                return
            }

            // Sort stories by chapter
            const stories = [...data]
            // stories.sort((_a, _b) =>
            // {
            //     return _a.acf.chapter.slug.localeCompare(_b.acf.chapter.slug)
            // })

            // Go through each story to find the current one and the next one
            let currentStory = null
            let nextStory = null

            for(let i = 0; i < stories.length; i++)
            {
                const _story = stories[i]

                // Slug match
                if(_story.acf.slug === this.$route.params.storySlug)
                {
                    // Current
                    currentStory = _story

                    // Next story (and make sure it's not a video)
                    let nextIndex = i + 1 < stories.length ? i + 1 : 0
                    nextStory = stories[nextIndex]

                    let dummy = 0
                    while(nextStory.acf.type.value === 'video' && dummy < 20)
                    {
                        nextIndex++

                        if(nextIndex > stories.length - 1)
                        {
                            nextIndex = 0
                        }

                        nextStory = stories[nextIndex]

                        dummy++
                    }
                }
            }

            // Story not found
            if(currentStory === null)
            {
                this.$router.push({ name: '404' })
                return
            }

            // Save current and next story
            this.story = currentStory
            this.nextStory = nextStory

            // SEO
            document.title = this.story.acf.seo_title ? this.story.acf.seo_title : this.story.acf.title.replace(/<br ?\/?>/gi, '')
            if(this.story.acf.seo_description)
            {
                document.querySelector('meta[name=description]').content = this.story.acf.seo_description
            }

            // Papillon
            this.$nextTick(() =>
            {
                window.dispatchEvent(new Event('papillon'))
            })

            // Header logo
            if(!this.$root.$previousRoute || this.$root.$previousRoute.name !== 'stories')
            {
                // Back to chapter
                this.$nextTick(() =>
                {
                    this.$eventHub.$emit('headerLogo.setState', { state: 'back', backText: this.story.acf.chapter.title, backTo: { name: `${this.$route.meta.lang}_chapter`, params: { chapterSlug: this.story.acf.chapter.slug } } })
                })
            }

            // Share links
            if(this.story.acf.share_links)
            {
                const url = `${this.$root.config.domain}${this.$route.meta.lang}/story/${this.story.acf.slug}`

                for(const _shareLink of this.story.acf.share_links)
                {
                    switch(_shareLink.social_network.value)
                    {
                        case 'facebook':
                            _shareLink.href = `https://www.facebook.com/sharer/sharer.php?u=${encodeURI(url)}`
                            break

                        case 'twitter':
                            _shareLink.href = `https://twitter.com/home?status=${encodeURI(_shareLink.tweet + ' ' + url)}`
                            break

                        case 'linkedin':
                            _shareLink.href = `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURI(url)}&title=${encodeURI(_shareLink.linkedin_title)}&summary=${encodeURI(_shareLink.linkedin_summary)}&source=`
                            break
                    }
                }
            }

            // Stroke
            this.$nextTick(() =>
            {
                this.onResize()

                this.stroke.$ctaPush = this.$refs.ctaPush

                if(!this.$root.config.platform.touch)
                {
                    this.stroke.$ctaPush.addEventListener('mouseenter', this.stroke.mouseenter)
                    this.stroke.$ctaPush.addEventListener('mouseleave', this.stroke.mouseleave)
                }

                this.stroke.bounding = this.stroke.$ctaPush.getBoundingClientRect()

                TweenLite.to(this.stroke, 1, { x: this.stroke.bounding.left, ease: Power2.easeOut })
            })
        }, this.$root.isFirstPage())
    },

    mounted()
    {
        // Stroke
        this.stroke = {}
        this.stroke.$ctaPush = null
        this.stroke.x = - 60
        this.stroke.length = 60
        this.stroke.lineWidth = 1
        this.stroke.strokeStyle = '#ffffff'
        this.stroke.bounding = null

        this.stroke.mouseenter = () =>
        {
            TweenLite.to(this.stroke, 0.5, { length: this.stroke.bounding.width, ease: Power2.easeOut })
        }

        this.stroke.mouseleave = () =>
        {
            TweenLite.to(this.stroke, 0.5, { length: 60, ease: Power2.easeOut })
        }

        this.$eventHub.$emit('line.createStroke', this.stroke)

        window.requestAnimationFrame(() =>
        {
            this.$eventHub.$emit('line.setElement', this.$refs.lineMark, true)
        })
    },

    destroyed()
    {
        if(!this.$root.config.platform.touch)
        {
            this.stroke.$ctaPush.removeEventListener('mouseenter', this.stroke.mouseenter)
            this.stroke.$ctaPush.removeEventListener('mouseleave', this.stroke.mouseleave)
        }

        let $eventHub = this.$eventHub
        let stroke = this.stroke

        TweenLite.to(this.stroke, 1, { x: this.sizes.width, length: 30, ease: Power2.easeInOut, onComplete()
        {
            $eventHub.$emit('line.deleteStroke', stroke)
            $eventHub = null
            stroke = null
        } })
    },

    methods:
    {
        onScroll()
        {
            if(this.sizes.viewport.width > 1200 && this.scroll.current > this.sizes.viewport.height && this.scroll.current < this.fullHeight - this.sizes.viewport.height * 1.5)
            {
                this.isShareVisible = true
            }
            else
            {
                this.isShareVisible = false
            }
        },

        onResize()
        {
            this.fullHeight = Math.max(document.body.scrollHeight, document.body.offsetHeight, document.documentElement.clientHeight, document.documentElement.scrollHeight, document.documentElement.offsetHeight)
        },

        onPlayerDeactivate()
        {
            this.$eventHub.$emit('line.setElement', this.$refs.lineMark, true)
        },

        onIntroPushClick()
        {
            this.animateTo(this.sizes.viewport.height, 1)
        },

        onPlayClick()
        {
            this.$eventHub.$emit('player.activate', this.story.acf.video.ID)
        },

        onVideoCoverPlay()
        {
            const time = Date.now()
            const delta = time - this.time
            const minTime = 2200

            if(delta < minTime)
            {
                window.setTimeout(() =>
                {
                    this.coverVideoPlaying = true
                }, minTime - delta)
            }
            else
            {
                this.coverVideoPlaying = true
            }
        },

        onVisualClick()
        {
            if(this.story.acf.video)
            {
                this.$eventHub.$emit('player.activate', this.story.acf.video.ID)
            }
        }
    }
}



// WEBPACK FOOTER //
// ./src/components/app-page-story/script.js